import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import useServerInfo from "../hooks/useServerInfo";
import { useChangeLanguage } from "../containers/Topbar/useLanguageMenu";

const FLAGS: Record<string, string> = {
  en: "\u{1F1FA}\u{1F1F8}",
  de: "\u{1F1E9}\u{1F1EA}",
  it: "\u{1F1EE}\u{1F1F9}",
  pt: "\u{1F1F5}\u{1F1F9}",
  fr: "\u{1F1EB}\u{1F1F7}",
  da: "\u{1F1E9}\u{1F1F0}",
  hr: "\u{1F1ED}\u{1F1F7}",
  cs: "\u{1F1E8}\u{1F1FF}",
  pl: "\u{1F1F5}\u{1F1F1}",
  es: "\u{1F1EA}\u{1F1F8}",
};

export default function DeepLinkLanguageSwitcher() {
  const { i18n } = useTranslation();
  const { data } = useServerInfo();
  const changeLanguage = useChangeLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const languages = data?.languages;
  const current = languages?.find((l) => l.id === i18n.language);
  const resources = i18n.options?.resources;

  const availableLanguages = resources
    ? Object.keys(resources)
        .map((id) => languages?.find((l) => l.id === id))
        .filter(Boolean)
    : [];

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        className="flex items-center gap-2 bg-white rounded-[10px] p-3 border-none cursor-pointer text-gray-800 font-medium text-sm shadow-sm hover:shadow-md transition-shadow"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="text-lg leading-none">{FLAGS[i18n.language] || FLAGS.en}</span>
        <span>{current?.name || "English"}</span>
        <svg
          className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 bg-white rounded-xl shadow-lg z-50 py-1 min-w-[180px] overflow-hidden">
            {availableLanguages.map((lang) => (
              <button
                key={lang!.id}
                className={`w-full flex items-center gap-3 px-4 py-2.5 border-none cursor-pointer text-sm text-left transition-colors ${
                  lang!.id === i18n.language ? "bg-gray-100 font-semibold" : "bg-white hover:bg-gray-50"
                }`}
                onClick={() => {
                  changeLanguage(lang!.id);
                  const qs = searchParams.toString();
                  navigate(`/${lang!.id}/mobile-app-install${qs ? `?${qs}` : ""}`);
                  setOpen(false);
                }}
              >
                <span className="text-lg leading-none">{FLAGS[lang!.id] || FLAGS.en}</span>
                <span className="text-gray-800">{lang!.name}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
